// src/services/StripeAdminService.js
// Admin service for fetching Stripe payment data

class StripeAdminService {
  /**
   * Fetch payment details from Stripe for a payment intent
   * @param {string} paymentIntentId - Stripe payment intent ID
   * @returns {Promise<Object>} - Payment intent details
   */
  static async getPaymentIntent(paymentIntentId) {
    try {
      const response = await fetch('/api/admin/stripe-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentIntentId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching payment intent:', error);
      return null;
    }
  }

  /**
   * Fetch multiple payment intents in batch
   * @param {Array<string>} paymentIntentIds - Array of payment intent IDs
   * @returns {Promise<Object>} - Map of payment intent ID to payment data
   */
  static async getPaymentIntents(paymentIntentIds) {
    try {
      const response = await fetch('/api/admin/stripe-payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentIntentIds }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching payment intents:', error);
      return {};
    }
  }

  /**
   * Get Stripe customer information by ID or email
   * @param {string} customerId - Stripe customer ID
   * @param {string} email - Customer email (alternative to customerId)
   * @returns {Promise<Object>} - Customer details with analytics
   */
  static async getCustomer(customerId, email = null) {
    try {
      const response = await fetch('/api/admin/stripe-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId, email }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Customer not found
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  }

  /**
   * Search for Stripe customers by email addresses
   * @param {Array<string>} emails - Array of email addresses to search
   * @returns {Promise<Object>} - Map of emails to customer data
   */
  static async searchCustomersByEmails(emails) {
    try {
      const response = await fetch('/api/admin/stripe-customer-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emails }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching customers:', error);
      return {
        customers: {},
        notFound: emails,
        summary: { found: 0, notFound: emails.length, total: emails.length }
      };
    }
  }

  /**
   * Link Firebase users with their Stripe customer data
   * @param {Array} users - Array of user objects with email addresses
   * @returns {Promise<Array>} - Users enriched with Stripe customer data
   */
  static async linkUsersWithStripeCustomers(users) {
    try {
      // Extract unique email addresses
      const emails = users
        .map(user => user.email)
        .filter(email => email && email.includes('@'));

      if (emails.length === 0) {
        return users; // No emails to search
      }

      console.log(`🔍 Searching Stripe for ${emails.length} user emails...`);

      // Search for Stripe customers
      const searchResults = await this.searchCustomersByEmails(emails);

      console.log(`✅ Found ${searchResults.summary.found} Stripe customers out of ${searchResults.summary.total} users`);

      // Enrich users with Stripe data
      const enrichedUsers = users.map(user => {
        const stripeCustomer = searchResults.customers[user.email];

        if (stripeCustomer) {
          return {
            ...user,
            stripeCustomer: {
              id: stripeCustomer.id,
              totalSpent: stripeCustomer.totalSpent,
              transactionCount: stripeCustomer.transactionCount,
              lastPayment: stripeCustomer.lastPayment,
              customerSince: stripeCustomer.created,
              averageTransactionAmount: stripeCustomer.transactionCount > 0
                ? stripeCustomer.totalSpent / stripeCustomer.transactionCount
                : 0
            },
            hasStripeAccount: true
          };
        }

        return {
          ...user,
          hasStripeAccount: false
        };
      });

      return enrichedUsers;
    } catch (error) {
      console.error('Error linking users with Stripe customers:', error);
      return users; // Return original users if linking fails
    }
  }

  /**
   * Get Stripe dashboard metrics for date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} - Revenue and transaction metrics
   */
  static async getDashboardMetrics(startDate, endDate) {
    try {
      const response = await fetch('/api/admin/stripe-metrics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startDate, endDate }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching metrics:', error);
      return {
        totalRevenue: 0,
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        refunds: 0
      };
    }
  }

  /**
   * Get refunds for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} - Refund data and summary
   */
  static async getRefunds(startDate, endDate) {
    try {
      const response = await fetch('/api/admin/stripe-refunds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startDate, endDate }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching refunds:', error);
      return {
        refunds: [],
        summary: {
          totalAmount: 0,
          count: 0,
          pending: 0,
          succeeded: 0,
          failed: 0
        }
      };
    }
  }

  /**
   * Perform comprehensive Stripe audit to find all payments and issues
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} - Complete audit data including orphaned payments
   */
  static async performStripeAudit(startDate, endDate) {
    try {
      const response = await fetch('/api/admin/stripe-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startDate, endDate }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error performing Stripe audit:', error);
      return {
        summary: {
          totals: { paymentIntents: 0, refunds: 0, uniqueCustomers: 0 },
          amounts: { totalGross: 0, totalRefunded: 0, totalNet: 0 },
          issues: { orphanedPayments: 0, testPayments: 0, paymentsWithRefunds: 0 }
        },
        payments: [],
        standaloneRefunds: [],
        customers: []
      };
    }
  }

  /**
   * Get comprehensive revenue analysis with Stripe data
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} - Comprehensive revenue data
   */
  static async getRevenueAnalysis(startDate, endDate) {
    try {
      const [metrics, refunds] = await Promise.all([
        this.getDashboardMetrics(startDate, endDate),
        this.getRefunds(startDate, endDate)
      ]);

      return {
        revenue: {
          gross: metrics.totalRevenue,
          net: metrics.netRevenue || (metrics.totalRevenue - refunds.summary.totalAmount),
          refunded: refunds.summary.totalAmount,
          fees: metrics.fees || 0
        },
        transactions: {
          total: metrics.totalTransactions,
          successful: metrics.successfulTransactions,
          failed: metrics.failedTransactions,
          refundCount: refunds.summary.count
        },
        refunds: refunds.summary,
        averages: {
          transactionAmount: metrics.averageTransactionAmount,
          refundAmount: refunds.summary.count > 0 ? refunds.summary.totalAmount / refunds.summary.count : 0
        },
        dateRange: {
          start: startDate,
          end: endDate
        }
      };
    } catch (error) {
      console.error('Error getting revenue analysis:', error);
      return {
        revenue: { gross: 0, net: 0, refunded: 0, fees: 0 },
        transactions: { total: 0, successful: 0, failed: 0, refundCount: 0 },
        refunds: { totalAmount: 0, count: 0 },
        averages: { transactionAmount: 0, refundAmount: 0 }
      };
    }
  }

  /**
   * Enrich booking data with Stripe payment information
   * @param {Array} bookings - Array of booking objects
   * @returns {Promise<Array>} - Bookings with enriched payment data
   */
  static async enrichBookingsWithStripeData(bookings) {
    try {
      // Extract unique payment intent IDs
      const paymentIntentIds = bookings
        .map(booking => booking.payment?.paymentIntentId || booking.paymentIntentId)
        .filter(id => id && id.startsWith('pi_'));

      if (paymentIntentIds.length === 0) {
        return bookings; // No Stripe payments to enrich
      }

      // Fetch payment data from Stripe
      const stripePayments = await this.getPaymentIntents(paymentIntentIds);

      // Also get refunds to check if any payments have been refunded
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const refundsData = await this.getRefunds(
        thirtyDaysAgo.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0]
      );

      // Create a map of payment intent ID to refunds
      const refundMap = {};
      refundsData.refunds.forEach(refund => {
        if (refund.paymentIntentId) {
          if (!refundMap[refund.paymentIntentId]) {
            refundMap[refund.paymentIntentId] = [];
          }
          refundMap[refund.paymentIntentId].push(refund);
        }
      });

      // Enrich bookings with Stripe data
      const enrichedBookings = bookings.map(booking => {
        const paymentIntentId = booking.payment?.paymentIntentId || booking.paymentIntentId;
        const stripeData = stripePayments[paymentIntentId];
        const refunds = refundMap[paymentIntentId] || [];

        if (stripeData) {
          const totalRefunded = refunds.reduce((sum, refund) => sum + refund.amount, 0);
          const netAmount = (stripeData.amount / 100) - totalRefunded;

          return {
            ...booking,
            stripePayment: stripeData,
            stripeRefunds: refunds,
            payment: {
              ...booking.payment,
              stripeStatus: stripeData.status,
              stripeAmount: stripeData.amount / 100, // Convert from cents
              stripeCurrency: stripeData.currency,
              stripeCreated: new Date(stripeData.created * 1000),
              stripePaymentMethod: stripeData.payment_method_details,
              stripeReceiptUrl: stripeData.receipt_url,
              stripeFees: stripeData.application_fee_amount ? stripeData.application_fee_amount / 100 : 0,
              stripeRefunded: totalRefunded,
              stripeNetAmount: netAmount,
              hasRefunds: refunds.length > 0
            }
          };
        }

        return booking;
      });

      return enrichedBookings;
    } catch (error) {
      console.error('Error enriching bookings with Stripe data:', error);
      return bookings; // Return original bookings if enrichment fails
    }
  }
}

export default StripeAdminService;